/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

import { isEqual, xor } from 'lodash-es';
import { SubjectViewModel, UnitPermissionsModel, SubjectServerModel } from './share-panel';

const pickProperties = (obj: any, keys: string[]) => Object.fromEntries(
  keys
    .filter((key) => key in obj)
    .map((key) => [key, obj[key]]),
);

const subjectPropsForEquality = ['id', 'privacyLevel', 'privateShareLink', 'path'];

const comparePermissionEqualityProps = (a: UnitPermissionsModel, b: UnitPermissionsModel): boolean => {
  const aUnitPermissions = pickProperties(a, subjectPropsForEquality);
  const bUnitPermissions = pickProperties(b, subjectPropsForEquality);
  return isEqual(aUnitPermissions, bUnitPermissions);
};

/**
 * Compares two permission objects and returns true if they are equal.
 * Only considers the properties would denote a meaningful difference.
 *
 * When adding permissions to a Unit, some properties of those permissions are not available.
 * This comparison method ignore those properties, as well as the order of the permissions.
 *
 * Things considered for uniqueness:
 * - PermissionsModel.permissions.length
 * - PermissionsModel.privacyLevel
 * - UnitPermissionsModel.id
 */
export const compareUnitPermissions = (a: UnitPermissionsModel, b: UnitPermissionsModel): boolean => {
  // If number of permissions are different, we can skip any further comparison.
  if (a.permissions.length !== b.permissions.length) return false;

  // Only consider unit permission IDs when comparing.
  const diff = xor(a.permissions.map((p) => p.id), b.permissions.map((p) => p.id));
  return comparePermissionEqualityProps(a, b) && diff.length === 0;
};

/**
 * Used in to map string values to booleans in response from server.
 * Some requests return "true" and "false" instead of true and false.
 */
export const setInheritedToBoolean = (resp: UnitPermissionsModel): UnitPermissionsModel => {
  const sanitizedResp = resp;
  sanitizedResp.permissions = resp.permissions.map((user: SubjectServerModel): SubjectViewModel => {
    const cleanUser = user;
    if (user.inherited === 'false') {
      cleanUser.inherited = false;
    } else if (user.inherited === 'true') {
      cleanUser.inherited = true;
    }
    return cleanUser;
  });
  return sanitizedResp;
};
